import { Logger } from "tslog";
import Delta from "quill-delta";
import { ContentElement, ElementType } from "./commonTypes";
import { Utils } from "./amplify-utils";


export class QuillUtils {
  private static log = new Logger({
    minLevel: Utils.GLOBAL_LOG_LEVEL
  });
  private static FORMAT_ATTRIBUTES = ['italic', 'bold', 'color'];
  private static HEADING_ATTRIBUTES = ['header'];

  public static parseStringQuill(content: string | null): Array<ContentElement> {
    if (!content) {
        return [];
    } else {
        const obj = JSON.parse(content);
        const elements = QuillUtils.parseQuill(obj.ops);

        // Use reduce to build the final array with replacements for PARAGRAPH elements
        const returnElements = elements.reduce((acc: ContentElement[], el: ContentElement) => {
            if (el.type === ElementType.TEXT_LINE) {
                // Parse the paragraph content to potentially split it into notes/chats
                const parsedElements = this.parseElements(el.content); // parseElements returns an array of Element
                return acc.concat(parsedElements); // Concatenate parsed elements
            } else {
                // If it's not a paragraph, add it as-is
                acc.push(el);
                return acc;
            }
        }, []);

        return returnElements;
    }
}

  // static parseElements(input: string): Element[] {
  //   // Extended regex to match both [note:name/title] and [chat:name/title] formats
  //   const elementRegex = /\[(note|chat):([^\/]+)\/([^\]]+)\]/g;
  //   let result: Element[] = [];

  //   // Track the last index to capture text between matches
  //   let lastIndex = 0;

  //   // Iterate over all matches
  //   let match;
  //   while ((match = elementRegex.exec(input)) !== null) {
  //     // Add any preceding text before the match as a PARAGRAPH element
  //     if (lastIndex < match.index) {
  //       result.push(new Element(ElementType.TEXT_LINE, input.slice(lastIndex, match.index)));
  //     }

  //     // Determine the element type based on the match
  //     const type = match[1] === 'note' ? ElementType.PERSONAL_NOTE : ElementType.GROUP_CHAT;

  //     // Add the matched element object
  //     const element = new Element(type, match[3]); // `content` is the title
  //     element.alias = match[2]; // `alias` is the name
  //     result.push(element);

  //     console.log(`I parsed a [${type}] [${element.alias}] [${element.content}]`);
  //     // Update lastIndex to the end of the current match
  //     lastIndex = match.index + match[0].length;
  //   }

  //   // Add any remaining text after the last match as a PARAGRAPH element
  //   if (lastIndex < input.length) {
  //     result.push(new Element(ElementType.TEXT_LINE, input.slice(lastIndex)));
  //   }

  //   return result;
  // }

  static parseElements(input: string): ContentElement[] {
    // Extended regex to match [note:name/title], [chat:name/title], and [video=path]
    const elementRegex = /\[(note|chat):([^\/]+)\/([^\]]+)\]|\[video=([^\]]+)\]/g;
    let result: ContentElement[] = [];
  
    // Track the last index to capture text between matches
    let lastIndex = 0;
  
    // Iterate over all matches
    let match;
    while ((match = elementRegex.exec(input)) !== null) {
      // Add any preceding text before the match as a TEXT_LINE element
      if (lastIndex < match.index) {
        result.push(new ContentElement(ElementType.TEXT_LINE, input.slice(lastIndex, match.index)));
      }
  
      if (match[1] === 'note' || match[1] === 'chat') {
        // Determine the element type based on the match
        const type = match[1] === 'note' ? ElementType.PERSONAL_NOTE : ElementType.GROUP_CHAT;
  
        // Add the matched element object
        const element = new ContentElement(type, match[3]); // `content` is the title
        element.alias = match[2]; // `alias` is the name

        //Clean alias (in case needed)
        const trash = '"},{"insert":"';
        if (element.alias.startsWith(trash)) {
          element.alias = element.alias.split(trash)[1];
        }
    
        result.push(element);
  
        console.log(`I parsed a [${type}] [${element.alias}] [${element.content}]`);
      } else if (match[4]) {
        // Handle [video=path] format
        const element = new ContentElement(ElementType.VIDEO, match[4]); // `content` is the video path
        result.push(element);
  
        console.log(`I parsed a [VIDEO] [${element.content}]`);
      }
  
      // Update lastIndex to the end of the current match
      lastIndex = match.index + match[0].length;
    }
  
    // Add any remaining text after the last match as a TEXT_LINE element
    if (lastIndex < input.length) {
      result.push(new ContentElement(ElementType.TEXT_LINE, input.slice(lastIndex)));
    }
  
    return result;
  }
  

  public static parseQuill(content: any): Array<ContentElement> {
    const delta = new Delta(content);
    /*  We must here make a distinction between a 
        a "block" ending and a line ending. A line
        ending can happen at any time during the 
        parsing process, including at "block endings".
        
        Heading and blockquote are "blocks", and 
        their endings can be identified by an "operation"
        with attributes (header/blockquote) and insert=\n.

        Other attribute types (font, color, italic, bold) are
        applicable only to the fragments within their context
        (i.e., their sibling insert element), and don't 
        necessarily include a line ending (but they could).
    */

    const elements = new Array<ContentElement>();

    var lineEnding = '\n';
    var currentContent: string = '';

    delta.forEach((op) => {

      if (op.insert) {

        const opContent = op.insert as string;

        /* Special case: header attributes and a single line ending
        => closing of a header or block quote => TODO */

        //Add opening formatting elements (<b>, <i>) if applicable
        let blockEndingCheck = QuillUtils.isBlockEnding(op);
        if (blockEndingCheck.isIt) {
          /*
          if this is the case, the formatting attributes apply to the whole
          fragment. What we need to do is to add the formatting to the fragment
          and append it to the current content. In case there is a line ending at the 
          end of the fragment, it means the block is finished too.
          */
          if (blockEndingCheck.header && blockEndingCheck.headerLevel) {
            const headerElement = new ContentElement(ElementType.HEADING, Utils.stripHtml(currentContent));
            headerElement.level = blockEndingCheck.headerLevel;
            elements.push(headerElement);
            currentContent = '';
          } else if (blockEndingCheck.blockquote) {
            const blockQuoteElement = new ContentElement(ElementType.BLOCKQUOTE, currentContent);
            elements.push(blockQuoteElement);
            currentContent = '';
          }
        } else if (QuillUtils.hasFormattingAttributes(op)) {
          //format fragment
          currentContent += QuillUtils.formatFragment(op);
        } else {
          //just a simple insert.
          //is it an image?
          if (op.insert instanceof Object) {
            if (op.insert['video']) {
              QuillUtils.log.debug("Hey, it's a video!");
              const videoUrl = op.insert['video'] as string;
              //Extract the video key
              //https://www.youtube.com/embed/Hca-ksSHvT8?showinfo=0',
              const regexp = /embed\/(.*)\?/gm;
              // const regexp = /v=(.*)/gm;

              const videoId = Array.from(videoUrl.matchAll(regexp), m => m[1]);
              // regexp.exec(videoUrl)?.groups


              // const videoId = matches.next().value;
              QuillUtils.log.debug(`Extracted video id [${videoId}]`);
              const videoElement = new ContentElement(ElementType.YOUTUBE_VIDEO, videoId[0]);
              elements.push(videoElement);

            } else {
              //probably an image => TODO,
              const insert = op.insert;
              const imgString = insert['image'] as string;
              const imageElement = new ContentElement(ElementType.BASE64_ENCODED_IMAGE, imgString);
              elements.push(imageElement);
            }
          } else {
            // append to content =>> BUT string split!!!!!!! do it

            for (let i = 0; i < opContent.length; i++) {
              if (opContent[i] == lineEnding) {
                //Create TextLineElement, add to the list of elements, flush
                //the bufer
                const textLineElement = new ContentElement(ElementType.TEXT_LINE, currentContent);
                elements.push(textLineElement);
                currentContent = '';
              }
              else {
                currentContent += opContent[i];
              }
            }
          }
        }
      } else {
        //Not insert, then what?
        QuillUtils.log.error('Strange operation, no insert', op);
      }
    });
    return elements;
  }

  public static hasFormattingAttributes(op: any) {
    if (op.attributes) {
      const attrKeys = Object.keys(op.attributes);
      //for each formatting attribute, check the operation attributes for a match
      if (QuillUtils.FORMAT_ATTRIBUTES.some(item => attrKeys.includes(item))) {
        return true;
      }
    }
    return false;
  }

  public static isBlockEnding(op: any): { isIt: boolean, header?: boolean, blockquote?: boolean, headerLevel?: number } {
    if (op.attributes) {
      const attrKeys = Object.keys(op.attributes);
      if (attrKeys.includes('header')) {
        const headerLevel = op.attributes.header;
        return { isIt: true, header: true, headerLevel: headerLevel };
      }
      if (attrKeys.includes('blockquote')) {
        return { isIt: true, blockquote: true };
      }
    }
    return { isIt: false };
  }

  private static formatFragment(deltaOperation: any): string {

    const bold = deltaOperation.attributes.bold;
    // const href = deltaOperation.attributes;
    const italic = deltaOperation.attributes.italic;
    // const blockquote = attributes.blockquote;

    let fragmentHtml = '';
    if (bold) {
      fragmentHtml += '<b>';
    }
    if (italic) {
      fragmentHtml += '<i>';
    }

    fragmentHtml += deltaOperation.insert;

    if (italic) {
      fragmentHtml += '</i>';
    }
    if (bold) {
      fragmentHtml += '</b>';
    }

    return fragmentHtml;
  }
}
